import './styles.scss';

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';

import { isBrowser } from 'Helpers/Browser';
import { redirect } from 'Helpers/Uri';
import { getUrlFilters } from 'Helpers/Search';
import { getParams } from 'Helpers/Search';
import { isPhotoInLocalStorageCart } from 'Helpers/Cart';
import { addParamToQuery } from 'Helpers/Uri';
import { hashCode } from 'Helpers/String';
import { getFullSlug } from 'Helpers/Photos';
import { getRouteByName } from 'Helpers/Routes';

import Head from 'Components/helpers/Head';
import GalleryHeader from 'Components/layout/GalleryHeader';
import Gallery from 'Components/layout/Gallery';
import GalleryNoResult from 'Components/layout/GalleryNoResult';
import Pagination from 'Components/layout/Pagination';
import filtersMap from 'Presets/searchFilters';

import { search, updatePhotos, setCurrentGalleryElement } from 'Redux/actions/photos';
import { addItem as cartAddItem } from 'Redux/actions/cart';
import { addItem as lightboxAddItem, removeItem as lightboxRemoveItem } from 'Redux/actions/lightbox';

@connect(
    (state) => ({
        photos: state.photos,
        user: state.user
    }), { search, updatePhotos, setCurrentGalleryElement, cartAddItem, lightboxAddItem, lightboxRemoveItem }
)
export default class Search extends React.PureComponent {
    static propTypes = {
        search: PropTypes.func.isRequired,
        cartAddItem: PropTypes.func.isRequired,
        lightboxAddItem: PropTypes.func.isRequired,
        lightboxRemoveItem: PropTypes.func.isRequired,
        updatePhotos: PropTypes.func.isRequired,
        setCurrentGalleryElement: PropTypes.func.isRequired,
        photos: PropTypes.shape({
            search: PropTypes.shape({
                isPending: PropTypes.bool.isRequired,
                isLoaded: PropTypes.bool.isRequired,
                lastPage: PropTypes.number.isRequired,
                totalCount: PropTypes.number.isRequired,
                params: PropTypes.shape({
                    query: PropTypes.string,
                    page: PropTypes.number,
                    businessId: PropTypes.string,
                    brandId: PropTypes.string
                }).isRequired,
                photos: PropTypes.arrayOf(PropTypes.object).isRequired
            })
        }).isRequired,
        user: PropTypes.shape({
            accessToken: PropTypes.string,
            currentBusinessId: PropTypes.string,
            currentBrandId: PropTypes.string,
            profile: PropTypes.shape({
                admin: PropTypes.bool.isRequired
            })
        }),
        params: PropTypes.shape({
            query: PropTypes.string,
            page: PropTypes.string
        }).isRequired,
        location: PropTypes.object
    };
    static defaultProps = {};

    state = {
        perPage: 40
    };

    componentDidMount = () => {
        this.loadPhotos(this.props);
    }

    componentWillReceiveProps = nextProps => {
        if (isBrowser() && this.isFetchRequired(nextProps)) {
            this.loadPhotos(nextProps);
        }
    }

    isFetchRequired = props => {
        const { query, pageNumber } = getParams(props.params);
        const { currentBusinessId, currentBrandId } = props.user;
        const { query: lastRequestedQuery, page: lastRequestedPage, businessId: lastRequestedBusinessId, brandId: lastRequestedBrandId } = props.photos.search.params;

        return query !== lastRequestedQuery || pageNumber !== lastRequestedPage || currentBusinessId !== lastRequestedBusinessId || currentBrandId !== lastRequestedBrandId;
    }

    loadPhotos = (props, filters) => {
        const { query, pageNumber } = getParams(props.params);

        if (this.props.photos.search.isPending) {
            return false;
        }

        let searchFilters = {};
        if (typeof filters !== 'undefined' && Object.keys(filters).length) {
            searchFilters = filters;
        } else {
            searchFilters = getUrlFilters(filtersMap, window.location);
        }

        this.props.search({
            query: query,
            page: pageNumber,
            businessId: props.user.currentBusinessId,
            brandId: props.user.currentBrandId,
            license: searchFilters && searchFilters.license ? searchFilters.license : null,
            rotation: searchFilters && searchFilters.orientation ? searchFilters.orientation : null,
            pixelSize: searchFilters && searchFilters.size ? searchFilters.size : null,
            perPage: this.state.perPage
        }).then(response => {
            setTimeout(() => {
                if (window.location.hash && document.querySelector(window.location.hash)) {
                    document.querySelector(window.location.hash).scrollIntoView({ behavior: 'auto' });
                }
            }, 300);
        });
    }

    onFiltersChange = newFilters => {
        const { query, pageNumber, pageString } = getParams(this.props.params);
        let baseUrl = getRouteByName('search', { query: query, page: pageNumber && pageNumber > 1 ? pageString : '' }).path;

        let filters = getUrlFilters(filtersMap, window.location);
        filters = { ...filters, ...newFilters };

        let newQuery = '';
        Object.keys(filters).forEach(filter => {
            newQuery = addParamToQuery(this.props.location, filter, filters[filter]);
        });

        this.loadPhotos(this.props, filters);
        redirect(baseUrl + newQuery);
    }

    setGalleryActiveElement = photo => {
        const { query, pageNumber, pageString } = getParams(this.props.params);
        this.props.setCurrentGalleryElement({
            returnUrl: '/search/:query/:page:filters:hash',
            query: query,
            page: pageNumber,
            perPage: this.state.perPage,
            filters: getUrlFilters(filtersMap, window.location),
            photo: photo,
            elements: this.props.photos.search.photos
        });
    }

    render() {
        const { isPending, isLoaded, photos, totalCount, lastPage } = this.props.photos.search;
        const { query, pageNumber, pageString } = getParams(this.props.params);
        let { perPage } = this.state;

        return (
            <div className="container-search">
                <Head
                    title={`Search results for ${query}`}
                    description={`Search results for ${query}`}
                    canonical={getRouteByName('search', { query: query, page: pageNumber && pageNumber > 1 ? pageString : '' }).path}
                    robots={'noindex, nofollow'}
                    relPrev={pageNumber > 1 ? getRouteByName('search', { query: query, page: pageString.replace(pageNumber, (pageNumber - 1)) }).path : ''}
                    relNext={pageNumber < lastPage ? getRouteByName('search', { query: query, page: pageString.replace(pageNumber, (pageNumber + 1)) }).path : ''}
                />
                <div className="container-fluid">
                    <GalleryHeader
                        header={query ? <span><strong>{query}</strong> photos ({!isPending && isLoaded ? totalCount : '-'} search results)</span> : null}
                        filters={filtersMap}
                        onFiltersChange={this.onFiltersChange}
                        location={this.props.location}
                    />
                    <Gallery
                        isLoading={isPending || !isLoaded}
                        noResultInfo={<GalleryNoResult query={query} page={pageNumber} />}
                        onEnterAsync={this.setGalleryActiveElement}
                        photos={photos.map(photo => ({
                            ...photo,
                            href: getRouteByName('singlePhoto', { slug: getFullSlug(photo) }).path,
                            controls: {
                                curate: {
                                    visible: this.props.user.profile && this.props.user.profile.admin
                                },
                                addToCart: {
                                    disabled: photo.isInCart || isPhotoInLocalStorageCart(photo),
                                    visible: !photo.downloadUrl && (!this.props.user.accessToken || photo.purchasable),
                                    trigger: photo => this.props.cartAddItem(photo, 'search')
                                        .then(this.props.updatePhotos([{ id: photo.id, payload: { isInCart: true } }]))
                                },
                                addToLightbox: {
                                    disabled: photo.isInLightbox,
                                    visible: !!this.props.user.accessToken && !photo.isInLightbox,
                                    trigger: photo => this.props.lightboxAddItem(photo, 'search')
                                        .then(this.props.updatePhotos([{ id: photo.id, payload: { isInLightbox: true } }]))
                                },
                                removeFromLightbox: {
                                    disabled: !photo.isInLightbox,
                                    visible: !!this.props.user.accessToken && photo.isInLightbox,
                                    trigger: photo => this.props.lightboxRemoveItem(photo, 'search')
                                        .then(this.props.updatePhotos([{ id: photo.id, payload: { isInLightbox: false } }]))
                                },
                                download: {
                                    disabled: false,
                                    visible: photo.downloadUrl
                                }
                            }
                        }))}
                    />
                    <Pagination
                        totalElements={parseInt(totalCount)}
                        elementsPerPage={parseInt(perPage)}
                        currentPage={pageNumber}
                        link={getRouteByName('search', { query: query, page: 'page-%pageNumber%' }).path}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/containers/global/Search/index.js